// ────────────────────────────────────────────────────────────────────────
// Cliente del microservicio de forecasting (microservice-forecasting),
// expuesto a través del BFF en /api/v1/forecasting/*.
//
// El backend resuelve el SCOPE multi-tenant por el rol del token (zero trust):
//   - superadministrator → global
//   - logística/empresa   → su propia entidad (forzado)
//   - holding             → lista de sus empresas
// Por eso el front NO envía scope/scopeId: solo pide la métrica/granularidad y
// el backend devuelve lo que el usuario puede ver. La respuesta envuelve los
// datos en { data: ... }.
//
// Contratos:
//   GET    forecasting/forecasts?metric&granularity[&from&to]   → { data: Serie[] }
//   GET    forecasting/forecasts/history?metric&granularity     → { data: SerieAcc[] }
//   GET    forecasting/events[?from&to]                         → { data: Event[] }
//   POST   forecasting/events                                   → { data: Event }
//   PUT    forecasting/events/:id                               → { data: Event }
//   DELETE forecasting/events/:id                               → { data: { id, deleted } }
// ────────────────────────────────────────────────────────────────────────
import Axios from "../config/axios-v1-config";

const BASE = "forecasting";

// Desempaqueta { data: ... } o el cuerpo directo (tolerante a ambas formas).
const unwrap = (res) => res?.data?.data ?? res?.data ?? null;
const unwrapList = (res) => {
  const data = res?.data?.data ?? res?.data ?? [];
  return Array.isArray(data) ? data : [];
};

/**
 * Series de proyección vigentes para el scope del usuario. Normalmente longitud 1
 * (varias solo en el caso holding = varias empresas).
 *
 * @param {object} opts
 * @param {"created"|"delivered"|"delivered_before_sla"} opts.metric
 * @param {"daily"|"weekly"|"monthly"} [opts.granularity="daily"]
 * @param {string} [opts.from] YYYY-MM-DD inclusive
 * @param {string} [opts.to]   YYYY-MM-DD inclusive
 * @returns {Promise<object[]>}
 */
export async function getForecasts({ metric, granularity = "daily", from, to } = {}) {
  const params = { metric, granularity };
  if (from) params.from = from;
  if (to) params.to = to;
  return unwrapList(await Axios.get(`${BASE}/forecasts`, { params }));
}

/**
 * Precisión (forecast_accuracy) por entidad. Por defecto (source=backtest) es el
 * backtest de la corrida vigente, que se reemplaza en cada corrida: el modelo de
 * hoy evaluado contra lo realmente ocurrido en fechas que no vio al entrenar.
 *
 * Cada serie trae summary { n, mae, mape, wape, biasRel }:
 *   - wape    Σ|error| / Σreal, fracción ≥ 0 (puede superar 1). Métrica principal:
 *             pondera por volumen.
 *   - biasRel (Σproyectado − Σreal) / Σreal; negativo = el backtest subestima. La
 *             vista no lo muestra: el backtest no lleva la rampa estacional del
 *             punto servido.
 *   - mape    promedio de errores relativos por día (ceros excluidos); se dispara
 *             con días de volumen bajo, se mantiene solo por compatibilidad.
 *   - mae     error absoluto medio, en unidades de la métrica.
 * wape, biasRel y mape son null cuando no hay volumen real para medirlos.
 * Sin rango devuelve los últimos 90 días (430 en mensual).
 * @returns {Promise<object[]>}
 */
export async function getForecastHistory({ metric, granularity = "daily", from, to } = {}) {
  const params = { metric, granularity };
  if (from) params.from = from;
  if (to) params.to = to;
  return unwrapList(await Axios.get(`${BASE}/forecasts/history`, { params }));
}

/**
 * Calendario de eventos visibles para el usuario (globales + los de su tenant).
 * @returns {Promise<object[]>}
 */
export async function listEvents({ from, to } = {}) {
  const params = {};
  if (from) params.from = from;
  if (to) params.to = to;
  return unwrapList(await Axios.get(`${BASE}/events`, { params }));
}

/**
 * Crea un evento. El scope (global / tenant) lo decide el backend según el rol;
 * no se envía desde el front en este flujo.
 * @param {{name:string, kind:"holiday"|"peak"|"promo"|"other", startDate:string, endDate?:string, note?:string}} body
 */
export async function createEvent(body) {
  return unwrap(await Axios.post(`${BASE}/events`, body));
}

export async function updateEvent(id, patch) {
  return unwrap(await Axios.put(`${BASE}/events/${id}`, patch));
}

/**
 * Sugerencia del factor de un evento a partir del histórico (mediana real/base en
 * días pasados que matchean el evento). El backend resuelve el scope por el token.
 * @returns {Promise<{suggestedEffect: number|null, samples: number}>}
 */
export async function getEffectSuggestion({ metric = "created", startDate, endDate, recurrence = "none" } = {}) {
  const params = { metric, startDate, recurrence };
  if (endDate) params.endDate = endDate;
  return unwrap(await Axios.get(`${BASE}/events/effect-suggestion`, { params }));
}

export async function deleteEvent(id) {
  return unwrap(await Axios.delete(`${BASE}/events/${id}`));
}
